import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { Save } from 'lucide-react';
import type { PricingConfig } from '../../types/pricing';

interface PricingConfigProps {
  config: PricingConfig;
  onUpdate: (config: PricingConfig) => void;
}

export function PricingConfig({ config, onUpdate }: PricingConfigProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { pricingConfig: config });
    } catch (err) {
      setError('Failed to save pricing configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    onUpdate({ ...config, [key]: numValue });
  };

  const updateNestedConfig = (category: string, key: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    onUpdate({
      ...config,
      [category]: {
        ...config[category as keyof PricingConfig],
        [key]: numValue
      }
    });
  };

  return (
    <div className="space-y-8">
      {error && (
        <div className="mb-4 p-4 rounded-md bg-red-50 text-red-700">{error}</div>
      )}

      {/* DTF Settings */}
      <div className="settings-section">
        <h4 className="text-lg font-medium text-default mb-6">DTF Settings</h4>
        <div className="settings-grid">
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Cost per Square Inch</label>
            <input
              type="number"
              value={config.decorationCostDTF}
              onChange={(e) => updateConfig('decorationCostDTF', e.target.value)}
              className="value-input"
              step="0.01"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Admin Time (minutes)</label>
            <input
              type="number"
              value={config.adminTimeDTF}
              onChange={(e) => updateConfig('adminTimeDTF', e.target.value)}
              className="value-input"
              step="0.1"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Design Time (minutes)</label>
            <input
              type="number"
              value={config.designTimeDTF}
              onChange={(e) => updateConfig('designTimeDTF', e.target.value)}
              className="value-input"
              step="0.1"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Setup Time (minutes)</label>
            <input
              type="number"
              value={config.setupTimeDTF}
              onChange={(e) => updateConfig('setupTimeDTF', e.target.value)}
              className="value-input"
              step="0.1"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Print Time (minutes)</label>
            <input
              type="number"
              value={config.printTimeDTF}
              onChange={(e) => updateConfig('printTimeDTF', e.target.value)}
              className="value-input"
              step="0.1"
            />
          </div>
        </div>
      </div>

      {/* Screen Printing Settings */}
      <div className="settings-section">
        <h4 className="text-lg font-medium text-default mb-6">Screen Printing Settings</h4>
        <div className="settings-grid">
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Cost per Square Inch</label>
            <input
              type="number"
              value={config.decorationCostScreenPrinting}
              onChange={(e) => updateConfig('decorationCostScreenPrinting', e.target.value)}
              className="value-input"
              step="0.01"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Admin Time (minutes)</label>
            <input
              type="number"
              value={config.adminTimeScreenPrinting}
              onChange={(e) => updateConfig('adminTimeScreenPrinting', e.target.value)}
              className="value-input"
              step="0.1"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Design Time (minutes)</label>
            <input
              type="number"
              value={config.designTimeScreenPrinting}
              onChange={(e) => updateConfig('designTimeScreenPrinting', e.target.value)}
              className="value-input"
              step="0.1"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Setup Time (minutes)</label>
            <input
              type="number"
              value={config.setupTimeScreenPrinting}
              onChange={(e) => updateConfig('setupTimeScreenPrinting', e.target.value)}
              className="value-input"
              step="0.1"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Print Time (minutes)</label>
            <input
              type="number"
              value={config.printTimeScreenPrinting}
              onChange={(e) => updateConfig('printTimeScreenPrinting', e.target.value)}
              className="value-input"
              step="0.1"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Min Quantity per Location</label>
            <input
              type="number"
              value={config.minQuantityPerLocation}
              onChange={(e) => updateConfig('minQuantityPerLocation', e.target.value)}
              className="value-input"
              min="1"
            />
          </div>
        </div>
      </div>

      {/* General Settings */}
      <div className="settings-section">
        <h4 className="text-lg font-medium text-default mb-6">General Settings</h4>
        <div className="settings-grid">
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Shop Rate ($/hour)</label>
            <input
              type="number"
              value={config.shopRate}
              onChange={(e) => updateConfig('shopRate', e.target.value)}
              className="value-input"
              step="0.01"
            />
          </div>
          <div className="settings-item">
            <label className="text-sm font-medium text-muted">Margin Multiplier</label>
            <input
              type="number"
              value={config.margin}
              onChange={(e) => updateConfig('margin', e.target.value)}
              className="value-input"
              step="0.01"
            />
          </div>
        </div>
      </div>

      {/* Apparel Base Prices */}
      <div className="settings-section">
        <h4 className="text-lg font-medium text-default mb-6">Apparel Base Prices</h4>
        <div className="settings-grid">
          {Object.entries(config.apparelPrices).map(([key, value]) => (
            <div key={key} className="settings-item">
              <label className="text-sm font-medium text-muted">
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </label>
              <input
                type="number"
                value={value}
                onChange={(e) => updateNestedConfig('apparelPrices', key, e.target.value)}
                className="value-input"
                step="0.01"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Style Modifiers */}
      <div className="settings-section">
        <h4 className="text-lg font-medium text-default mb-6">Style Modifiers</h4>
        <div className="settings-grid">
          {Object.entries(config.styleModifiers).map(([key, value]) => (
            <div key={key} className="settings-item">
              <label className="text-sm font-medium text-muted">
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </label>
              <input
                type="number"
                value={value}
                onChange={(e) => updateNestedConfig('styleModifiers', key, e.target.value)}
                className="value-input"
                step="0.01"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Handling Fees */}
      <div className="settings-section">
        <h4 className="text-lg font-medium text-default mb-6">Handling Fees</h4>
        <div className="settings-grid">
          {Object.entries(config.handlingFees).map(([key, value]) => (
            <div key={key} className="settings-item">
              <label className="text-sm font-medium text-muted">
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </label>
              <input
                type="number"
                value={value}
                onChange={(e) => updateNestedConfig('handlingFees', key, e.target.value)}
                className="value-input"
                step="0.01"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}